import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Center, Spinner, Text, VStack, Alert, AlertIcon } from '@chakra-ui/react';
import { Technician, ServiceArea } from '../services/firestore';

interface TechnicianMapProps {
  technicians: Technician[];
  selectedTechnician?: Technician;
  onTechnicianSelect: (technician: Technician) => void;
  showServiceAreas?: boolean;
  centerOnSelected?: boolean;
}

interface MapMarker {
  technician: Technician;
  marker: google.maps.Marker;
  infoWindow?: google.maps.InfoWindow;
}

const TechnicianMap: React.FC<TechnicianMapProps> = ({
  technicians,
  selectedTechnician,
  onTechnicianSelect,
  showServiceAreas = true,
  centerOnSelected = true
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<MapMarker[]>([]);
  const serviceAreaCirclesRef = useRef<google.maps.Circle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get Maps API key using the same logic as ServiceArea component
  const getMapsApiKey = (): string => {
    // Try direct key first
    const direct = (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    if (direct) {
      console.log('[TechnicianMap] Using direct REACT_APP_GOOGLE_MAPS_API_KEY');
      return direct;
    }

    // Try per-environment keys if provided
    const envFromStorage = typeof window !== 'undefined' ? (localStorage.getItem('selectedEnvironment') || '').toLowerCase() : '';
    const reactAppEnv = ((process as any).env.REACT_APP_ENV || '').toLowerCase();
    const nodeEnv = ((process as any).env.NODE_ENV || '').toLowerCase();
    
    // Use localStorage preference, or build-time env, or NODE_ENV as fallback
    let buildEnv = envFromStorage || reactAppEnv;
    
    // If no explicit REACT_APP_ENV, use NODE_ENV (Vercel sets this automatically)
    if (!buildEnv && nodeEnv === 'production') {
      buildEnv = 'production';
    } else if (!buildEnv) {
      buildEnv = 'development';
    }
    
    const envName = buildEnv.toLowerCase();

    const byEnv: Record<string, string | undefined> = {
      development: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_DEV,
      preview: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_PREVIEW,
      production: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_PROD,
    };
    
    const selectedKey = byEnv[envName];
    console.log('[TechnicianMap] Resolving Maps API Key:', { 
      envFromStorage, 
      reactAppEnv, 
      nodeEnv, 
      buildEnv, 
      envName, 
      hasKey: !!selectedKey,
      keyPreview: selectedKey ? `${selectedKey.slice(0, 10)}...${selectedKey.slice(-4)}` : 'none'
    });
    
    return selectedKey || '';
  };

  // Initialize map instance - separate function so it can be called from multiple places
  const initMapInstance = useCallback(() => {
    if (!mapRef.current) {
      console.log('⚠️ Map ref not ready');
      return;
    }

    if (!window.google || !window.google.maps) {
      console.log('⚠️ Google Maps not available');
      return;
    }

    if (mapInstanceRef.current) {
      console.log('✅ Map already initialized');
      setIsLoading(false);
      return;
    }

    try {
      console.log('🗺️ Initializing map instance...');
      const defaultCenter = { lat: 33.7490, lng: -84.3880 }; // Atlanta, GA
      
      const map = new window.google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: 12,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      mapInstanceRef.current = map;
      setIsLoading(false);
      console.log('✅ Google Maps initialized successfully');
    } catch (err) {
      console.error('❌ Error initializing map:', err);
      setError('Failed to initialize map.');
      setIsLoading(false);
    }
  }, []);

  // Initialize Google Maps using the same approach as ServiceArea component
  const initializeMap = useCallback(async () => {
    console.log('🗺️ initializeMap called', { 
      hasWindow: typeof window !== 'undefined',
      hasMapRef: !!mapRef.current,
      hasGoogle: !!(window as any).google,
      hasGoogleMaps: !!(window as any).google?.maps
    });
    
    if (typeof window === 'undefined') {
      console.log('⚠️ Window not available');
      return;
    }
    
    if (!mapRef.current) {
      console.log('⚠️ mapRef.current not available yet, will retry...');
      // Retry after a short delay
      setTimeout(() => {
        if (mapRef.current) {
          initializeMap();
        }
      }, 100);
      return;
    }

    const apiKey = getMapsApiKey();
    if (!apiKey) {
      setError('Google Maps API key not found. Please check your environment variables. Make sure REACT_APP_GOOGLE_MAPS_API_KEY or environment-specific keys are set.');
      setIsLoading(false);
      return;
    }

    // Check if Google Maps is already loaded (e.g., from ServiceArea component)
    if (window.google && window.google.maps) {
      console.log('✅ Google Maps already loaded, initializing map...');
      // Use a small delay to ensure DOM is ready
      setTimeout(() => {
        initMapInstance();
      }, 100);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.getElementById('gmaps-js') || document.getElementById('gmaps-js-technician');
    if (existingScript) {
      console.log('⏳ Waiting for existing script to load...');
      // Wait for it to load
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkLoaded);
          initMapInstance();
        }
      }, 100);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkLoaded);
        if (window.google && window.google.maps) {
          initMapInstance();
        }
      }, 5000);
      return;
    }

    // Load Google Maps script
    try {
      const envName = typeof window !== 'undefined' ? (localStorage.getItem('selectedEnvironment') || '').toLowerCase() : '';
      const buildEnv = ((process as any).env.REACT_APP_ENV || 'development').toLowerCase();
      const currentEnv = (envName || buildEnv);
      
      console.log('[Maps] Loading JS API', { envName: currentEnv, key: `${apiKey.substring(0, 5)}...${apiKey.substring(apiKey.length - 5)}` });

      const script = document.createElement('script');
      script.id = 'gmaps-js-technician';
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&v=weekly`;
      
      script.onload = () => {
        console.log('✅ Script loaded, initializing map...');
        if (window.google && window.google.maps) {
          initMapInstance();
        }
      };
      
      script.onerror = () => {
        console.error('❌ Failed to load Google Maps JS');
        setError('Failed to load Google Maps. Please check your API key and internet connection.');
        setIsLoading(false);
      };
      
      document.body.appendChild(script);

      // Fallback timeout in case onload doesn't fire
      setTimeout(() => {
        if (window.google && window.google.maps && !mapInstanceRef.current && mapRef.current) {
          console.log('⏰ Fallback timeout: initializing map...');
          initMapInstance();
        }
      }, 2000);
    } catch (err) {
      console.error('❌ Error loading Google Maps:', err);
      setError('Failed to load Google Maps. Please check your API key and internet connection.');
      setIsLoading(false);
    }
  }, [initMapInstance]);

  // Create technician markers and service area markers
  const createMarkers = useCallback(() => {
    if (!mapInstanceRef.current || !window.google || !window.google.maps) return;

    // Clear existing markers
    markersRef.current.forEach(({ marker, infoWindow }) => {
      marker.setMap(null);
      infoWindow?.close();
    });
    markersRef.current = [];

    // Track bounds to center map appropriately
    const bounds = new window.google.maps.LatLngBounds();
    let hasValidLocation = false;

    technicians.forEach((technician) => {
      // Show service area centers as markers (for configuration)
      if (technician.serviceAreas && technician.serviceAreas.length > 0) {
        technician.serviceAreas.forEach((serviceArea, areaIndex) => {
          if (serviceArea.center && serviceArea.center.latitude && serviceArea.center.longitude) {
            const position = { 
              lat: serviceArea.center.latitude, 
              lng: serviceArea.center.longitude 
            };
            
            bounds.extend(position);
            hasValidLocation = true;
            
            const marker = new window.google.maps.Marker({
              position,
              map: mapInstanceRef.current,
              title: `${technician.firstName} ${technician.lastName} - ${serviceArea.name || 'Service Area'}`,
              icon: {
                url: getMarkerIcon(technician),
                scaledSize: new window.google.maps.Size(32, 32),
                anchor: new window.google.maps.Point(16, 32)
              },
              label: {
                text: serviceArea.name || `${technician.firstName[0]}${technician.lastName[0]}`,
                color: '#fff',
                fontSize: '10px',
                fontWeight: 'bold'
              },
              zIndex: 1 // Service area markers below location markers
            });

            const infoWindow = new window.google.maps.InfoWindow({
              content: createInfoWindowContent(technician, serviceArea)
            });

            marker.addListener('click', () => {
              onTechnicianSelect(technician);
              if (mapInstanceRef.current) {
                infoWindow.open(mapInstanceRef.current, marker);
              }
            });

            markersRef.current.push({ technician, marker, infoWindow });
          }
        });
      }
      
      // ALSO show real-time location markers when available (for operational monitoring)
      if (technician.currentLocation?.latitude && technician.currentLocation?.longitude) {
        const position = { 
          lat: technician.currentLocation.latitude, 
          lng: technician.currentLocation.longitude 
        };
        
        bounds.extend(position);
        hasValidLocation = true;
        
        // Use a different icon style for location markers (e.g., with a location pin)
        const locationMarker = new window.google.maps.Marker({
          position,
          map: mapInstanceRef.current,
          title: `${technician.firstName} ${technician.lastName} - Current Location`,
          icon: {
            url: getLocationMarkerIcon(technician),
            scaledSize: new window.google.maps.Size(40, 40), // Slightly larger for location
            anchor: new window.google.maps.Point(20, 40)
          },
          animation: technician.isOnline ? window.google.maps.Animation.BOUNCE : undefined,
          zIndex: 2 // Location markers above service area markers
        });

        const locationInfoWindow = new window.google.maps.InfoWindow({
          content: createInfoWindowContent(technician, undefined, true) // Pass flag for location
        });

        locationMarker.addListener('click', () => {
          onTechnicianSelect(technician);
          if (mapInstanceRef.current) {
            locationInfoWindow.open(mapInstanceRef.current, locationMarker);
          }
        });

        markersRef.current.push({ technician, marker: locationMarker, infoWindow: locationInfoWindow });
      }
    });

    // Fit bounds to show all markers, or use default center if no valid locations
    if (!mapInstanceRef.current) return;
    
    if (hasValidLocation && markersRef.current.length > 0) {
      mapInstanceRef.current.fitBounds(bounds);
      // Ensure minimum zoom level
      const listener = window.google.maps.event.addListener(mapInstanceRef.current, 'bounds_changed', () => {
        if (mapInstanceRef.current) {
          const currentZoom = mapInstanceRef.current.getZoom();
          if (currentZoom && currentZoom > 15) {
            mapInstanceRef.current.setZoom(15);
          }
        }
        window.google.maps.event.removeListener(listener);
      });
    } else {
      // Default center if no technicians or locations
      mapInstanceRef.current.setCenter({ lat: 33.7490, lng: -84.3880 }); // Atlanta, GA
      mapInstanceRef.current.setZoom(10);
    }
  }, [technicians, onTechnicianSelect]);

  // Create service area circles
  const createServiceAreas = useCallback(() => {
    if (!mapInstanceRef.current || !showServiceAreas || !window.google || !window.google.maps) return;

    // Clear existing circles
    serviceAreaCirclesRef.current.forEach(circle => circle.setMap(null));
    serviceAreaCirclesRef.current = [];

    technicians.forEach((technician) => {
      technician.serviceAreas?.forEach((serviceArea) => {
        if (!serviceArea.center || !serviceArea.center.latitude || !serviceArea.center.longitude) {
          return; // Skip invalid service areas
        }
        
        const circle = new window.google.maps.Circle({
          strokeColor: serviceArea.color || '#3182CE',
          strokeOpacity: 0.3,
          strokeWeight: 2,
          fillColor: serviceArea.color || '#3182CE',
          fillOpacity: 0.1,
          map: mapInstanceRef.current,
          center: { lat: serviceArea.center.latitude, lng: serviceArea.center.longitude },
          radius: (serviceArea.radius || 5) * 1000, // Convert km to meters
          clickable: false
        });

        serviceAreaCirclesRef.current.push(circle);
      });
    });
  }, [technicians, showServiceAreas]);

  // Center map on selected technician
  const centerOnTechnician = useCallback(() => {
    if (!mapInstanceRef.current || !selectedTechnician || !centerOnSelected) return;

    const technicianMarker = markersRef.current.find(
      m => m.technician.id === selectedTechnician.id
    );

    if (technicianMarker) {
      mapInstanceRef.current.panTo(technicianMarker.marker.getPosition()!);
      mapInstanceRef.current.setZoom(14);
      
      // Open info window
      technicianMarker.infoWindow?.open(mapInstanceRef.current, technicianMarker.marker);
    }
  }, [selectedTechnician, centerOnSelected]);

  // Get marker icon for service area centers
  const getMarkerIcon = (technician: Technician): string => {
    if (!technician.isActive) {
      return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="14" fill="#E53E3E" stroke="#FFFFFF" stroke-width="2"/>
          <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold">SA</text>
        </svg>
      `);
    }
    
    if (technician.isOnline) {
      return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="14" fill="#38A169" stroke="#FFFFFF" stroke-width="2"/>
          <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold">SA</text>
        </svg>
      `);
    }
    
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="14" fill="#3182CE" stroke="#FFFFFF" stroke-width="2"/>
        <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold">SA</text>
      </svg>
    `);
  };

  // Get marker icon for real-time location (location pin style)
  const getLocationMarkerIcon = (technician: Technician): string => {
    const fillColor = !technician.isActive ? '#E53E3E' : technician.isOnline ? '#38A169' : '#3182CE';
    
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 0C12.27 0 6 6.27 6 14c0 10.5 14 26 14 26s14-15.5 14-26C34 6.27 27.73 0 20 0zm0 19c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" fill="${fillColor}" stroke="#FFFFFF" stroke-width="2"/>
      </svg>
    `);
  };

  // Create info window content
  const createInfoWindowContent = (technician: Technician, serviceArea?: ServiceArea, isLocation?: boolean): string => {
    const statusColor = technician.isOnline ? '#38A169' : technician.isActive ? '#3182CE' : '#E53E3E';
    const statusText = technician.isOnline ? 'Online' : technician.isActive ? 'Offline' : 'Inactive';
    
    return `
      <div style="padding: 8px; min-width: 200px;">
        <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">
          ${technician.firstName} ${technician.lastName}
        </div>
        ${isLocation ? `
          <div style="font-size: 12px; color: #38A169; margin-bottom: 4px; font-weight: 600;">
            📍 Current Location
          </div>
          ${technician.currentLocation?.address ? `
            <div style="font-size: 11px; color: #666; margin-bottom: 4px;">
              ${technician.currentLocation.address}
            </div>
          ` : ''}
          ${technician.currentLocation?.timestamp ? `
            <div style="font-size: 10px; color: #999; margin-bottom: 4px;">
              Updated: ${new Date(technician.currentLocation.timestamp.seconds * 1000).toLocaleString()}
            </div>
          ` : ''}
        ` : serviceArea ? `
          <div style="font-size: 12px; color: #3182CE; margin-bottom: 4px; font-weight: 600;">
            📍 ${serviceArea.name || 'Service Area'}
          </div>
          <div style="font-size: 11px; color: #666; margin-bottom: 4px;">
            Radius: ${serviceArea.radius || 0} km
          </div>
        ` : ''}
        <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
          ${technician.email}
        </div>
        <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
          ${technician.phone}
        </div>
        <div style="margin: 4px 0;">
          <span style="background-color: ${statusColor}; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px;">
            ${statusText}
          </span>
        </div>
        <div style="font-size: 11px; color: #666;">
          Rating: ${technician.rating || 0} ⭐ (${technician.totalReviews || 0} reviews)
        </div>
        <div style="font-size: 11px; color: #666;">
          Rate: $${technician.hourlyRate || 0}/hr
        </div>
        ${technician.skills && technician.skills.length > 0 ? `
          <div style="font-size: 11px; color: #666; margin-top: 4px;">
            Skills: ${technician.skills.slice(0, 3).join(', ')}${technician.skills.length > 3 ? '...' : ''}
          </div>
        ` : ''}
        ${technician.serviceAreas && technician.serviceAreas.length > 1 ? `
          <div style="font-size: 11px; color: #666; margin-top: 4px;">
            ${technician.serviceAreas.length} service area${technician.serviceAreas.length > 1 ? 's' : ''}
          </div>
        ` : ''}
      </div>
    `;
  };

  // Initialize map on mount
  useEffect(() => {
    console.log('🗺️ useEffect for map initialization triggered', { 
      hasMapRef: !!mapRef.current,
      isLoading 
    });
    initializeMap();
    
    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up map...');
      // Clear markers
      markersRef.current.forEach(({ marker, infoWindow }) => {
        try {
          marker.setMap(null);
          infoWindow?.close();
        } catch (e) {
          // Ignore errors during cleanup
        }
      });
      markersRef.current = [];
      
      // Clear service area circles
      serviceAreaCirclesRef.current.forEach(circle => {
        try {
          circle.setMap(null);
        } catch (e) {
          // Ignore errors during cleanup
        }
      });
      serviceAreaCirclesRef.current = [];
      
      // Clear map instance but don't try to remove the DOM node
      // Google Maps may have modified it
      mapInstanceRef.current = null;
    };
  }, [initializeMap, isLoading]);

  // Update markers when technicians change
  useEffect(() => {
    if (mapInstanceRef.current) {
      createMarkers();
      createServiceAreas();
    }
  }, [technicians, createMarkers, createServiceAreas]);

  // Center on selected technician
  useEffect(() => {
    centerOnTechnician();
  }, [selectedTechnician, centerOnTechnician]);

  return (
    <Box 
      h="600px" 
      borderRadius="lg" 
      position="relative"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
    >
      {/* Map container - Google Maps will inject its own DOM here */}
      <Box
        ref={mapRef}
        h="100%"
        w="100%"
        position="absolute"
        top="0"
        left="0"
      />
      {error && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={1000}
          bg="white"
        >
          <Center h="full">
            <Alert status="error" maxW="400px">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold">Map Loading Error</Text>
                <Text fontSize="sm">{error}</Text>
              </VStack>
            </Alert>
          </Center>
        </Box>
      )}
      {isLoading && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={999}
          bg="white"
        >
          <Center h="full">
            <VStack spacing={4}>
              <Spinner size="lg" color="blue.500" />
              <Text>Loading Google Maps...</Text>
            </VStack>
          </Center>
        </Box>
      )}
    </Box>
  );
};

export default TechnicianMap; 